import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { BasicDto, IdObject } from "../../models/basicDto";

export class FormModel<T extends Object> extends FormGroup {
  static negativeNumberPattern = "^-?[0-9]+(.[0-9]+)?$";
  private _model: T;
  constructor(model: T, controls: { [key: string]: AbstractControl<any> }) {
    super(controls);
    this._model = model;
  }

  get model(): T {
    return this._model;
  }

  disableValidation(ignoreFields: string[]) {
    for (var key in this.controls) {
      if (ignoreFields.indexOf(key) == -1) {
        this.get(key)?.clearValidators();
        this.get(key)?.updateValueAndValidity();
      }
    }
  }

  removeNullProperties(obj: any): void {
    for (const prop in obj) {
      if (obj[prop] === null) {
        delete obj[prop];
      }
    }
  }

  disableValidationForField(...controls: (FormControl | FormArray)[]) {
    for (let control of controls) {
      control.clearValidators();
      control.setErrors(null); // <-- Is dit nodig?
      control.updateValueAndValidity();
    }
  }

  disableControl(...controls: FormControl[]) {
    for (let control of controls) {
      control.disable();
    }
  }

  enableControl(...controls: FormControl[]) {
    for (let control of controls) {
      control.enable();
    }
  }

  findInvalidFields(formGroup: FormGroup | FormArray = this): string[] {
    const invalidFields: string[] = [];

    Object.keys(formGroup.controls).forEach((fieldName) => {
      const control = formGroup.get(fieldName);

      if (control instanceof FormControl) {
        if (control.invalid) {
          invalidFields.push(fieldName);
        }
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        const subInvalidFields = this.findInvalidFields(control);

        if (subInvalidFields.length > 0) {
          invalidFields.push(
            ...subInvalidFields.map((subFieldName) => `${fieldName}.${subFieldName}`)
          );
        }
      }
    });
    return invalidFields;
  }

  setIsRequired(...controls: (FormControl | FormArray)[]) {
    for (let control of controls) {
      this.setRequired(control);
    }
  }

  setIsNotRequired(...controls: (FormControl | FormArray)[]) {
    for (let control of controls) {
      this.setRequired(control, false);
    }
  }

  setRequired(control: FormControl | FormArray, isrequired: Boolean = true) {
    if (isrequired) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    //control.updateValueAndValidity()
    control.updateValueAndValidity({ emitEvent: false });
  }

  mapToModel(): T {
    let modelCopy: T = { ...this.model } as T;
    Object.assign(modelCopy, { ...this.getRawValue() } as T);
    return modelCopy;
  }

  setModel(model: T) {
    this._model = model;
    this.patchValue(this.model);
  }
}

declare module "@angular/forms" {
  interface FormControl {
    resetValidators(): void;
    setUpdateOn(updateOn: "change" | "blur" | "submit"): void;
  }

  interface FormArray<TControl extends AbstractControl<any> = any> {
    typedControls(): TControl[];
  }

  interface FormGroup {
    mergeWith(otherFormGroup: FormGroup): FormGroup;
  }
}

export class IdObjectFormModel<T extends IdObject<number>> extends FormModel<T> {
  constructor(model: T, formGroup: FormGroup) {
    formGroup.addControl("Id", new FormControl(model.Id ?? 0));
    formGroup.addControl("isSelected", new FormControl(model.isSelected ?? false));
    super(model, formGroup.controls);
  }

  readonly Id = this.get("Id") as FormControl<number>;
  readonly isSelected = this.get("isSelected") as FormControl<boolean>;
}

export class BasicDtoFormModel<
  T extends BasicDto<any> = BasicDto<any>,
> extends IdObjectFormModel<T> {
  constructor(model: T, formGroup: FormGroup = new FormGroup({})) {
    formGroup.addControl("Name", new FormControl(model.Name));
    super(model, formGroup);
  }

  readonly Name = this.get("Name") as FormControl<string>;
}
FormControl.prototype.resetValidators = function () {
  this.clearValidators();
  this.setErrors(null);
  this.updateValueAndValidity({ emitEvent: false });
};
FormArray.prototype.typedControls = function <TControl>() {
  return this.controls as TControl[];
};

FormControl.prototype.setUpdateOn = function (updateOn: "change" | "blur" | "submit"): void {
  const currentValue = this.value;
  const currentValidators = this.validator;
  const currentAsyncValidators = this.asyncValidator;

  const newControl = new FormControl(currentValue, {
    validators: currentValidators ? [currentValidators] : [],
    asyncValidators: currentAsyncValidators ? [currentAsyncValidators] : [],
    updateOn: updateOn,
  });

  // Update the current instance with the new control properties
  this.setValidators(newControl.validator);
  this.setAsyncValidators(newControl.asyncValidator);
  this.updateValueAndValidity({ onlySelf: true, emitEvent: false });

  // Set the internal `_updateOn` property (this is an internal Angular property and should be used with caution)
  (this as any)._updateOn = updateOn;
};

FormGroup.prototype.mergeWith = function (otherFormGroup: FormGroup): FormGroup {
  Object.keys(otherFormGroup.controls).forEach((key) => {
    if (!this.contains(key)) {
      this.addControl(key, otherFormGroup.get(key)!);
    }
  });

  return this;
};

export type IForm<T> = {
  [K in keyof T]?: any;
};
